<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" v-on:click.self="closeModal">
        <transition name="slide-in">
          <div v-if="showModal" class="modal-panel">
            <header class="modal-head">
              <div>
                <div class="modal-eyebrow">
                  <span class="status-dot"></span>
                  My profile
                </div>
                <h2>My Profile</h2>
                <p>Update your name and contact details.</p>
              </div>
              <button type="button" class="icon-button" v-on:click="closeModal" aria-label="Close">
                <font-awesome-icon icon="fa-solid fa-xmark" />
              </button>
            </header>

            <Form class="modal-form" v-on:submit="saveProfile">
              <div class="modal-body">
                <section class="form-section">
                  <h3>Personal Details</h3>
                  <div class="form-grid">
                    <label class="field">
                      <span>First Name</span>
                      <Field
                        type="text"
                        name="fname"
                        v-model.trim="profileObj.fname"
                        placeholder="John"
                        :rules="{ required: ['First Name'] }"
                      />
                      <ErrorMessage name="fname" class="form-text text-danger" />
                    </label>

                    <label class="field">
                      <span>Last Name</span>
                      <Field
                        type="text"
                        name="lname"
                        v-model.trim="profileObj.lname"
                        placeholder="Doe"
                        :rules="{ required: ['Last Name'] }"
                      />
                      <ErrorMessage name="lname" class="form-text text-danger" />
                    </label>

                    <label class="field full">
                      <span>Email</span>
                      <Field
                        type="email"
                        name="email"
                        v-model.trim="profileObj.email"
                        placeholder="john@example.com"
                        :rules="{ email: ['Email'] }"
                      />
                      <ErrorMessage name="email" class="form-text text-danger" />
                    </label>

                    <label class="field full">
                      <span>Phone Number</span>
                      <div class="phone-row">
                        <select
                          v-model="selectedCountryCode"
                          class="country-code-select"
                          aria-label="Country code"
                        >
                          <option
                            v-for="country in countryCodes"
                            :key="country.iso"
                            :value="country.code"
                          >
                            {{ country.label }}
                          </option>
                        </select>
                        <input
                          v-model.trim="phoneLocalNumber"
                          type="tel"
                          placeholder="Phone number"
                          autocomplete="tel-national"
                          v-on:input="onPhoneInput"
                        />
                      </div>
                      <small v-if="phoneError" class="form-text text-danger">{{ phoneError }}</small>
                      <small class="hint">Used to reach you and to sign in with your phone number.</small>
                    </label>
                  </div>
                </section>
              </div>

              <footer class="modal-foot">
                <button type="button" class="ghost-button" v-on:click="closeModal">Cancel</button>
                <button
                  type="submit"
                  class="primary-button"
                  :disabled="accountIsLoading"
                >
                  <span v-if="!accountIsLoading">Save Changes</span>
                  <span v-else>Saving...</span>
                </button>
              </footer>
            </Form>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { Field, Form, ErrorMessage, defineRule } from "vee-validate";
import { email } from "@vee-validate/rules";
import { UPDATE_ACCOUNT } from "@/store/action_types";
import { TOAST_GLOBAL_CONFIG } from "@/utils/const";
import { COUNTRY_CODES, normalizePhoneNumber, splitPhoneNumber } from "@/utils/phone";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

library.add(faXmark);

defineRule("email", email);
defineRule("required", (value, [fieldName = "Field"]) => {
  if (value === null || value === undefined || String(value).trim().length === 0) {
    return `${fieldName} is required`;
  }
  return true;
});

export default {
  components: { Field, Form, ErrorMessage },
  emits: ["close-profile-modal"],
  data() {
    return {
      showModal: true,
      countryCodes: COUNTRY_CODES,
      selectedCountryCode: "+251",
      phoneLocalNumber: "",
      phoneError: "",
      profileObj: {
        fname: null,
        lname: null,
        email: null,
      },
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    accountIsLoading() {
      return this.$store.getters.account.isLoading;
    },
  },
  methods: {
    onPhoneInput() {
      if (this.phoneError) this.phoneError = "";
    },
    closeModal() {
      this.$emit("close-profile-modal");
    },
    saveProfile() {
      let phoneNumber = "";
      if (this.phoneLocalNumber && this.phoneLocalNumber.trim()) {
        phoneNumber = normalizePhoneNumber(this.selectedCountryCode, this.phoneLocalNumber);
        if (phoneNumber.replace(/\D/g, "").length < 8) {
          this.phoneError = "Enter a valid phone number.";
          return;
        }
      }

      const emailValue = (this.profileObj.email || "").trim();

      // The account must stay reachable by at least one identifier (mirrors the backend).
      if (!emailValue && !phoneNumber) {
        this.$toast.error("Provide an email or a phone number.", TOAST_GLOBAL_CONFIG);
        return;
      }

      const payload = {
        fname: this.profileObj.fname,
        lname: this.profileObj.lname,
        email: emailValue || null,
        phoneNumber: phoneNumber || null,
      };

      this.$store.dispatch(UPDATE_ACCOUNT, payload).then((res) => {
        if (res) {
          this.$toast.success("Profile updated.", TOAST_GLOBAL_CONFIG);
          this.closeModal();
        }
      });
    },
  },
  mounted() {
    const current = this.account || {};
    this.profileObj.fname = current.fname ?? null;
    this.profileObj.lname = current.lname ?? null;
    this.profileObj.email = current.email ?? null;

    const { code, local } = splitPhoneNumber(current.phoneNumber);
    this.selectedCountryCode = code;
    this.phoneLocalNumber = local;
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
}

.modal-panel {
  --dashboard-blue: #73c41d;
  --dashboard-blue-dark: #538c13;
  --dashboard-blue-soft: #eef6df;
  --dashboard-blue-border: #cfe6a3;
  --dashboard-slate: #34404a;
  --dashboard-muted: #76828b;
  --dashboard-border: #e3e8e6;
  --dashboard-canvas: #f4f6f3;
  --dashboard-green: #4aa12a;
  --dashboard-red: #e0464d;
  width: 480px;
  max-width: 95vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-left: 1px solid var(--dashboard-border);
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
  color: var(--dashboard-slate);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--dashboard-border);
}

.modal-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 5px 11px;
  margin-bottom: 8px;
  border: 1px solid var(--dashboard-border);
  border-radius: var(--ehs-radius-pill);
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--dashboard-green);
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12);
}

.modal-head h2 {
  margin: 0;
  font-family: var(--ehs-font-display);
  font-size: 1.4rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

.modal-head p {
  margin: 4px 0 0;
  color: var(--dashboard-muted);
  font-size: 0.86rem;
}

.icon-button {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--dashboard-border);
  background: #ffffff;
  border-radius: 50%;
  color: var(--dashboard-slate);
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.icon-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.modal-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 32px;
  background: var(--dashboard-canvas);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid var(--dashboard-border);
  border-radius: 16px;
}

.form-section h3 {
  margin: 0 0 14px;
  font-family: var(--ehs-font-display);
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.full {
  grid-column: 1 / -1;
}

.field > span {
  color: var(--dashboard-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.field input,
.field select,
.phone-row input,
.phone-row select {
  padding: 10px 12px;
  border: 1px solid var(--dashboard-border);
  border-radius: 10px;
  background: #ffffff;
  color: var(--dashboard-slate);
  font-size: 0.86rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field input:focus,
.field select:focus,
.phone-row input:focus,
.phone-row select:focus {
  border-color: var(--dashboard-blue);
  box-shadow: 0 0 0 3px rgba(115, 196, 29, 0.18);
}

.phone-row {
  display: grid;
  grid-template-columns: minmax(110px, 0.4fr) minmax(0, 1fr);
  gap: 10px;
}

.country-code-select {
  padding-left: 10px !important;
  padding-right: 10px !important;
}

.form-text.text-danger {
  margin-top: 4px;
  color: var(--dashboard-red);
  font-size: 0.74rem;
  font-weight: 500;
  position: relative;
  left: 4px;
}

.hint {
  margin-top: 6px;
  color: var(--dashboard-muted);
  font-size: 0.74rem;
  line-height: 1.5;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--dashboard-border);
  background: #ffffff;
}

.ghost-button,
.primary-button {
  border-radius: var(--ehs-radius-pill);
  border: 1px solid transparent;
  padding: 10px 20px;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: none !important;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.ghost-button {
  background: #ffffff;
  color: var(--dashboard-slate);
  border-color: var(--dashboard-border);
}

.ghost-button:hover {
  background: var(--dashboard-blue-soft);
  color: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-border);
}

.primary-button {
  background: var(--dashboard-blue);
  color: #ffffff;
  border-color: var(--dashboard-blue);
}

.primary-button:hover {
  background: var(--dashboard-blue-dark);
  border-color: var(--dashboard-blue-dark);
}

.primary-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

@media (max-width: 991.98px) {
  .modal-panel {
    width: 100%;
    max-width: 100%;
  }

  .modal-head {
    padding: 16px 16px 14px;
    gap: 10px;
  }

  .modal-head h2 {
    font-size: 1.2rem;
  }

  .modal-head p {
    font-size: 0.8rem;
  }

  .modal-body {
    padding: 16px 14px 24px;
  }

  .form-section {
    padding: 14px 14px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-foot {
    padding: 12px 14px;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .modal-foot .ghost-button,
  .modal-foot .primary-button {
    width: 100%;
  }
}
</style>
